import { Typography } from 'antd';

import { TRIAL_DAYS } from '../../billing/shared/trial';

import type {
  ReplaceDealStagesBodyDtoStagesItem,
  UpdateOrganizationOnboardingBodyDto,
} from '../../../api';
import type { ReactNode } from 'react';

export const ONBOARDING_STEP_FORM_ID = 'onboarding-step-form';

export type OnboardingStepKey =
  | 'profile'
  | NonNullable<UpdateOrganizationOnboardingBodyDto['currentStep']>;

export type OnboardingSurveyField = {
  name: keyof UpdateOrganizationOnboardingBodyDto;
  label: string;
  requiredMessage: string;
  options: { value: string; label: string }[];
  extra?: string;
};

const STAGE_COLORS = ['#F0F0F1', '#FDECC8', '#D3E5EF', '#E8DEEE', '#DBEDDB', '#FFE2DD'];
const buildStages = (names: string[]): ReplaceDealStagesBodyDtoStagesItem[] =>
  names.map((name, index) => ({
    name,
    color: STAGE_COLORS[index % STAGE_COLORS.length],
    kind: 'active',
  }));

export const onboardingBusinessTypeOptions: {
  value: NonNullable<UpdateOrganizationOnboardingBodyDto['businessType']>;
  label: string;
  optionLabel: string;
  stages: ReplaceDealStagesBodyDtoStagesItem[];
}[] = [
  {
    value: 'services',
    label: 'Services',
    optionLabel: 'Client services — agencies, consulting, legal, accounting',
    stages: buildStages(['New lead', 'Qualifying', 'Proposal sent', 'Won', 'Ongoing work']),
  },
  {
    value: 'subscription',
    label: 'Subscription',
    optionLabel: 'Software & subscriptions — SaaS, memberships, media',
    stages: buildStages(['Free trial', 'Signed up', 'Growing account', 'Renewal']),
  },
  {
    value: 'two_sided',
    label: 'Two-Sided / Match',
    optionLabel: 'Matching people — recruiting, staffing, marketplaces',
    stages: buildStages(['Sourcing', 'Submitted', 'Matched', 'Placed']),
  },
  {
    value: 'inventory',
    label: 'Inventory Deals',
    optionLabel: 'Big-ticket sales — real estate, autos, equipment',
    stages: buildStages(['New lead', 'Showing or quote', 'Offer made', 'Closed']),
  },
  {
    value: 'high_velocity',
    label: 'High-Velocity / Application',
    optionLabel: 'Applications & approvals — lending, insurance, solar',
    stages: buildStages(['New lead', 'Contacted', 'Qualified', 'Application in', 'Approved']),
  },
  {
    value: 'quote_to_order',
    label: 'Quote-to-Order / Commerce',
    optionLabel: 'Products & orders — e-commerce, wholesale, contractors',
    stages: buildStages(['Inquiry', 'Quote sent', 'Order placed', 'Delivered', 'Repeat customer']),
  },
  {
    value: 'appointment',
    label: 'Appointment / Booking',
    optionLabel: 'Appointments & bookings — clinics, salons, events, education',
    stages: buildStages(['Inquiry', 'Booked', 'Visit done', 'Rebooked']),
  },
];

export const onboardingRoleOptions: {
  value: NonNullable<UpdateOrganizationOnboardingBodyDto['role']>;
  label: string;
}[] = [
  { value: 'sales', label: 'Sales' },
  { value: 'founder', label: 'Founder' },
  { value: 'operations', label: 'Operations' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'customer_success', label: 'Support' },
  { value: 'other', label: 'Other' },
];

export const onboardingTeamSizeOptions: {
  value: NonNullable<UpdateOrganizationOnboardingBodyDto['teamSize']>;
  label: string;
}[] = [
  { value: 'just_me', label: 'Just me' },
  { value: '2_10', label: '2–10' },
  { value: '11_50', label: '11–50' },
  { value: '51_200', label: '51–200' },
  { value: '200_plus', label: '200+' },
];

export type OnboardingStepMeta = {
  key: OnboardingStepKey;
  title: string;
  subtitle: ReactNode;
  back: boolean;
  skip: boolean;
  submitLabel: string;
  surveyFields?: OnboardingSurveyField[];
};

export const onboardingSteps: OnboardingStepMeta[] = [
  {
    key: 'profile',
    title: 'Tell us about you',
    subtitle: "Your name and where your team's deals and contacts live.",
    back: false,
    skip: false,
    submitLabel: 'Continue',
  },
  {
    key: 'business-type',
    title: 'What kind of business do you run?',
    subtitle: "Pick the closest match and we'll set up your deal stages.",
    back: true,
    skip: false,
    submitLabel: 'Continue',
  },
  {
    key: 'about-you',
    title: 'Your role and team',
    subtitle: 'This tunes K3X to how you work.',
    back: true,
    skip: false,
    submitLabel: 'Continue',
    surveyFields: [
      {
        name: 'role',
        label: 'Your role',
        requiredMessage: 'Please select your role to continue.',
        options: onboardingRoleOptions,
      },
      {
        name: 'teamSize',
        label: 'Team size',
        requiredMessage: 'Please select your team size to continue.',
        options: onboardingTeamSizeOptions,
      },
    ],
  },
  ...(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    ? [
        {
          key: 'subscription' as const,
          title: 'Start your free trial',
          subtitle: `${TRIAL_DAYS} days free, full access from day one. No charge until the trial ends.`,
          back: true,
          skip: false,
          submitLabel: 'Continue',
        },
      ]
    : []),
  {
    key: 'workspace',
    title: 'Connect email and calendar',
    subtitle: 'K3X logs emails and meetings to the right contacts automatically.',
    back: true,
    skip: true,
    submitLabel: 'Continue',
  },
  {
    key: 'mcp',
    title: 'A cool way to manage K3X :-)',
    subtitle: (
      <>
        Run <Typography.Text strong>K3X</Typography.Text> natively inside Claude or ChatGPT
      </>
    ),
    back: true,
    skip: true,
    submitLabel: 'Finish setup',
  },
];

export const onboardingStepIndex = (key: OnboardingStepKey): number =>
  onboardingSteps.findIndex((step) => step.key === key);

export const nextOnboardingStep = (key: OnboardingStepKey): Exclude<OnboardingStepKey, 'profile'> =>
  (onboardingSteps[onboardingStepIndex(key) + 1]?.key ?? 'complete') as Exclude<
    OnboardingStepKey,
    'profile'
  >;

export const previousOnboardingStep = (key: OnboardingStepKey): OnboardingStepKey =>
  onboardingSteps[Math.max(onboardingStepIndex(key) - 1, 0)]!.key;
